import React from 'react';
import HomeCardAspect from '@components/Shared/HomeCards/Standard/HomeCardAspect';
import ChevronRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-right.svg';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import { SlideshowVendorStyles } from '@components/Shared/Slideshow/SlideshowVendorStyles.styled';
import { getUrlTarget, getSlug, isUrlInternal } from '@utils/utils';
import { STICKER_TYPES } from '@utils/stickerUtils';
import { renderDynamicContentPage } from '@utils/DynamicPageContent';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { FindAHomeContentfulData } from 'types/FindAHome/FindAHomeContentfulData';
import { PerformanceModelsSearchResponse } from 'types/FindAHome/PerformanceModels';
import { useHomeCardAspectRatio } from '@hooks/FindAHome/useHomeCardAspectRatio';
import { moveInReadySerialNumbers } from '@utils/moveInReadyHideImagesList';
import { CTAS_PER_PAGE } from '@utils/homeSearchConstants';

type Props = {
  homeList: PerformanceModelsSearchResponse;
  data: FindAHomeContentfulData;
  page: number;
};

type DynamicContent = {
  contentCTAProps: {
    headingText: string;
    image: string;
    buttonUrl: string;
  }[];
};

export default function HomeCardsList({
  homeList,
  data,
  page,
}: Props): JSX.Element {
  const dynamicContent = renderDynamicContentPage(data) as DynamicContent;
  const { aspectRatio } = useHomeCardAspectRatio();

  type Model = (typeof homeList.models)[0] & { isCta: false };
  type Cta = { isCta: true; ctaIndex: number };
  const homeListWithCtas: (Model | Cta)[] = [...homeList.models].map(
    (home) => ({ ...home, isCta: false })
  );

  const pageIndex = page - 1;
  const ctaTypesCount = dynamicContent.contentCTAProps.length;
  const ctaIndex1 = (pageIndex * CTAS_PER_PAGE) % ctaTypesCount;
  const ctaIndex2 = (ctaIndex1 + 1) % ctaTypesCount;

  // If there are less than 6 homes, tack on a CTA at the end, so we always see at least one.
  if (homeListWithCtas.length < 6) {
    homeListWithCtas.push({ isCta: true, ctaIndex: ctaIndex1 });
  } else {
    homeListWithCtas.splice(6, 0, { isCta: true, ctaIndex: ctaIndex1 });
  }

  if (homeListWithCtas.length > 18) {
    homeListWithCtas.splice(18, 0, { isCta: true, ctaIndex: ctaIndex2 });
  }

  type DxFavoritesContextState = {
    state: {
      favoriteModels: { modelNumber: string }[];
    };
    actions: {
      toggleFavoriteModel: (modelNumber: string) => void;
    };
  };

  const handleDesignCenterCTAClick = React.useCallback(() => {
    pushGTMEvent(
      'ga_home_srch_design_center_cta',
      'home_search_design_center_cta',
      {
        location: 'Home Search',
        category: 'brand_education',
        action: 'image',
        label: 'home_search_design_center_cta',
      }
    );
  }, []);

  const handleCTAClick = React.useCallback(() => {
    pushGTMEvent('ga_home_search_cta', 'home_search_cta', {
      location: 'Home Search',
      category: 'brand_education',
      action: 'image',
      label: 'home_search_cta',
    });
  }, []);

  return (
    <DXFavoritesContext.Consumer>
      {(props) => {
        const { state, actions } = props as unknown as DxFavoritesContextState;

        return (
          <div className="home-cards-listing" key="home-card-listing">
            {homeListWithCtas.map((home, i) => {
              return (
                <React.Fragment key={`home-card-fragment-${i}`}>
                  {!home.isCta && (
                    <div className="card-area-content card-area-card">
                      <SlideshowVendorStyles>
                        <HomeCardAspect
                          key={`$home-card-${home.modelNumber}`}
                          homeObject={home}
                          description={home.name}
                          imageAspectRatio={aspectRatio}
                          images={
                            home.isOnLand &&
                            home.images.length > 0 &&
                            !moveInReadySerialNumbers.includes(
                              home.serialNumber
                            )
                              ? [...home.images, ...home.thumbnailImages].slice(
                                  0,
                                  5
                                )
                              : home.thumbnailImages?.slice(0, 5)
                          }
                          cardLink={
                            home.isOnLand
                              ? `/homes/${home.serialNumber}/?isOnLand=${home.isOnLand}`
                              : `/homes/${home.modelNumber}/`
                          }
                          useSlideshow={true}
                          showFavoritingHeart={true}
                          isInStock={
                            !home.isOnLand
                              ? home.isInStockInArea
                              : home.isOnLand
                          }
                          dynamicHeight={false}
                          showStockIndicator={true}
                          imageQueryString={`?width=${aspectRatio.width}&fm=webp`}
                          isFavorited={
                            !!state.favoriteModels?.filter(
                              (faveModels) =>
                                faveModels.modelNumber === home.modelNumber
                            )[0]
                          }
                          handleFavoriteClick={(e) => {
                            e.preventDefault();
                            actions.toggleFavoriteModel(home.modelNumber);
                            pushGTMEvent('clickFavoritesHeart');
                          }}
                          showIndicators={true}
                          /* If home with land, add move-in ready sticker */
                          stickers={home.isOnLand ? [STICKER_TYPES[1]] : []}
                        />
                      </SlideshowVendorStyles>
                    </div>
                  )}
                  {home.isCta &&
                    dynamicContent?.contentCTAProps[home.ctaIndex]
                      ?.headingText && (
                      <div
                        className="card-area-content card-area-cta"
                        key={`home-card-content-${i}`}
                      >
                        <HomeCardAspect
                          key={`home-card-${i}`}
                          imageAspectRatio={aspectRatio}
                          homeObject={{
                            modelDescription: (
                              <>
                                <div className="heading-text">
                                  {
                                    dynamicContent.contentCTAProps[
                                      home.ctaIndex
                                    ].headingText
                                  }
                                </div>
                                <div className="chevron">
                                  <ChevronRightSvg />
                                </div>
                              </>
                            ),
                            altTextDescription:
                              dynamicContent.contentCTAProps[home.ctaIndex]
                                .headingText,
                          }}
                          images={[
                            {
                              reference:
                                dynamicContent.contentCTAProps[home.ctaIndex]
                                  .image,
                            },
                          ]}
                          imageQueryString={`?w=${aspectRatio.width}&fm=webp`}
                          forceExternalLink={
                            !isUrlInternal(
                              dynamicContent.contentCTAProps[home.ctaIndex]
                                .buttonUrl
                            )
                          }
                          cardLink={
                            isUrlInternal(
                              dynamicContent.contentCTAProps[home.ctaIndex]
                                .buttonUrl
                            )
                              ? getSlug(
                                  dynamicContent.contentCTAProps[home.ctaIndex]
                                    .buttonUrl
                                )
                              : dynamicContent.contentCTAProps[home.ctaIndex]
                                  .buttonUrl
                          }
                          cardLinkTarget={getUrlTarget(
                            dynamicContent.contentCTAProps[home.ctaIndex]
                              .buttonUrl
                          )}
                          useSlideshow={false}
                          showFavoritingHeart={false}
                          showStockIndicator={false}
                          onClick={
                            dynamicContent.contentCTAProps[
                              home.ctaIndex
                            ].buttonUrl.includes(
                              'designcenter.claytonhomes.com'
                            )
                              ? handleDesignCenterCTAClick
                              : handleCTAClick
                          }
                        />
                      </div>
                    )}
                </React.Fragment>
              );
            })}

            {/* make sure there are always 3 flex items (even if some have to be empty) or else the layout looks stupid */}
            {homeListWithCtas.length % 3 === 1 ? (
              <>
                <div></div>
                <div></div>
              </>
            ) : null}
            {homeListWithCtas.length % 3 === 2 ? (
              <>
                <div></div>
              </>
            ) : null}
          </div>
        );
      }}
    </DXFavoritesContext.Consumer>
  );
}
